import { useEffect, useRef, useState } from "react";

import type { ServiceError } from "../../service/Shared";

export type DiscussionVariant = "seed" | "seed-sharp" | "hub";

export type DiscussionFont = "figtree" | "space-grotesk";

export interface DiscussionPermissions {
  /** May post new threads and replies */
  createResource: boolean;
  /** May resolve threads (defaults to true when omitted) */
  resolveThread?: boolean;
}

export interface DiscussionReplyData {
  uuid: string;
  authorName: string;
  authorUuid: string;
  date: string;
  content: string;
  isUnread?: boolean;
}

export interface DiscussionThreadData {
  uuid: string;
  authorName: string;
  authorUuid: string;
  date: string;
  content: string;
  isUnread?: boolean;
  isResolved: boolean;
  replies: DiscussionReplyData[];
}

export type DiscussionsHeaderSize = "default" | "small";

export type DiscussionToastType = "success" | "error";

export interface DiscussionToast {
  id: number;
  type: DiscussionToastType;
  text: string;
}

export const MAX_DISCUSSION_LENGTH = 500;

export interface DiscussionsPanelProps {
  threads: DiscussionThreadData[];
  variant?: DiscussionVariant;
  /**
   * Font family for the panel. Required. Asset Manager and Development Manager
   * use `"figtree"`; every other role uses `"space-grotesk"`.
   */
  font: DiscussionFont;
  headerSize?: DiscussionsHeaderSize;
  /** Show shimmer skeleton instead of content */
  isLoading?: boolean;
  permissions: DiscussionPermissions;
  currentUserUuid: string;
  /**
   * Initial value for the "Show resolved" toggle.
   * Pass `true` to open the resolved section on mount (e.g. read from a URL
   * param or a persisted user preference). Defaults to `false`.
   */
  defaultShowResolved?: boolean;
  /**
   * Called whenever the user toggles "Show resolved discussions", with the
   * new boolean value. Use this to persist the preference to the API, a URL
   * param, localStorage, etc.
   */
  onShowResolvedChange?: (value: boolean) => void;
  onShowMore: () => void;
  onShowMoreResolved: () => void;
  /** Set to true when the server has more active threads beyond what's been fetched locally. */
  hasMoreFromServer?: boolean;
  /** Set to true when the server has more resolved threads beyond what's been fetched locally. */
  hasMoreResolvedFromServer?: boolean;
  onAddComment: (content: string) => void | Promise<void>;
  onReply: (threadUuid: string, content: string) => void | Promise<void>;
  onEditThreadMessage: (threadUuid: string, content: string) => void | Promise<void>;
  onEditReply: (threadUuid: string, replyUuid: string, content: string) => void | Promise<void>;
  onDeleteThread: (threadUuid: string) => void | Promise<void>;
  onDeleteReply: (threadUuid: string, replyUuid: string) => void | Promise<void>;
  onResolveThread: (threadUuid: string) => void | Promise<void>;
  onUnresolveThread?: (threadUuid: string) => void | Promise<void>;
  errors?: ServiceError[];
}

export type DiscussionEditState =
  | { type: "thread"; threadUuid: string; draft: string }
  | { type: "reply"; threadUuid: string; replyUuid: string; draft: string };

export type DiscussionDeleteTarget =
  | { type: "thread"; threadUuid: string }
  | { type: "reply"; threadUuid: string; replyUuid: string };

export interface UseDiscussionsPanelResult {
  showResolved: boolean;
  openThreadUuid: string | null;
  currentUserUuid: string;
  hasMoreThreads: boolean;
  hasMoreResolved: boolean;
  newCommentText: string;
  replyDrafts: Record<string, string>;
  editState: DiscussionEditState | null;
  pendingDelete: DiscussionDeleteTarget | null;
  activeThreads: DiscussionThreadData[];
  resolvedThreads: DiscussionThreadData[];
  canCreate: boolean;
  canResolve: boolean;
  toasts: DiscussionToast[];
  toggleShowResolved: () => void;
  handleOpenThread: (uuid: string) => void;
  handleShowMore: () => void;
  handleShowMoreResolved: () => void;
  handleNewCommentChange: (value: string) => void;
  handleAddComment: () => void;
  handleReplyDraftChange: (threadUuid: string, value: string) => void;
  handleSaveReply: (threadUuid: string) => void;
  handleCancelReply: (threadUuid: string) => void;
  handleStartEditThread: (threadUuid: string, currentContent: string) => void;
  handleStartEditReply: (threadUuid: string, replyUuid: string, currentContent: string) => void;
  handleEditDraftChange: (value: string) => void;
  handleSaveEdit: () => void;
  handleCancelEdit: () => void;
  handleDeleteThread: (threadUuid: string) => void;
  handleDeleteReply: (threadUuid: string, replyUuid: string) => void;
  handleConfirmDelete: () => void;
  handleCancelDelete: () => void;
  handleResolveThread: (threadUuid: string) => void;
  handleUnresolveThread: (threadUuid: string) => void;
  dismissToast: (id: number) => void;
}

const TOAST_DURATION_MS = 4000;

export const useDiscussionsPanel = ({
  threads,
  permissions,
  currentUserUuid,
  defaultShowResolved = false,
  onShowResolvedChange,
  onShowMore,
  onShowMoreResolved,
  hasMoreFromServer = false,
  hasMoreResolvedFromServer = false,
  onAddComment,
  onReply,
  onEditThreadMessage,
  onEditReply,
  onDeleteThread,
  onDeleteReply,
  onResolveThread,
  onUnresolveThread = () => undefined,
}: DiscussionsPanelProps): UseDiscussionsPanelResult => {
  const [showResolved, setShowResolved] = useState(defaultShowResolved);
  const [openThreadUuid, setOpenThreadUuid] = useState<string | null>(null);
  const [newCommentText, setNewCommentText] = useState("");
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  const [editState, setEditState] = useState<DiscussionEditState | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DiscussionDeleteTarget | null>(null);
  const [toasts, setToasts] = useState<DiscussionToast[]>([]);

  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const activeThreads = threads.filter((t) => !t.isResolved);
  const resolvedThreads = threads.filter((t) => t.isResolved);
  const hasMoreThreads = hasMoreFromServer;
  const hasMoreResolved = hasMoreResolvedFromServer;

  const showToast = (type: DiscussionToastType, text: string): void => {
    if (!mountedRef.current) return;
    const id = Date.now();
    setToasts((prev) => [...prev, { id, type, text }]);
    setTimeout(() => {
      if (mountedRef.current) {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }
    }, TOAST_DURATION_MS);
  };

  const dismissToast = (id: number): void => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const toggleShowResolved = (): void => {
    setShowResolved((prev) => {
      const next = !prev;
      onShowResolvedChange?.(next);
      return next;
    });
  };

  const handleOpenThread = (uuid: string): void => {
    setOpenThreadUuid((prev) => (prev === uuid ? null : uuid));
  };

  const handleShowMore = (): void => {
    onShowMore();
  };

  const handleShowMoreResolved = (): void => {
    onShowMoreResolved();
  };

  const handleNewCommentChange = (value: string): void => setNewCommentText(value);

  const handleAddComment = (): void => {
    const trimmed = newCommentText.trim();
    if (!trimmed || trimmed.length > MAX_DISCUSSION_LENGTH) return;
    setNewCommentText("");
    Promise.resolve(onAddComment(trimmed))
      .then(() => {
        showToast("success", "Comment added");
      })
      .catch(() => {
        showToast("error", "Failed to add comment");
      });
  };

  const handleReplyDraftChange = (threadUuid: string, value: string): void => {
    setReplyDrafts((prev) => ({ ...prev, [threadUuid]: value }));
  };

  const handleSaveReply = (threadUuid: string): void => {
    const draft = replyDrafts[threadUuid];
    if (!draft?.trim() || draft.trim().length > MAX_DISCUSSION_LENGTH) return;
    const trimmed = draft.trim();
    setReplyDrafts((prev) => ({ ...prev, [threadUuid]: "" }));
    Promise.resolve(onReply(threadUuid, trimmed))
      .then(() => {
        showToast("success", "Reply saved");
      })
      .catch(() => {
        showToast("error", "Failed to save reply");
      });
  };

  const handleCancelReply = (threadUuid: string): void => {
    setReplyDrafts((prev) => ({ ...prev, [threadUuid]: "" }));
    setOpenThreadUuid(null);
  };

  const handleStartEditThread = (threadUuid: string, currentContent: string): void => {
    setEditState({ type: "thread", threadUuid, draft: currentContent });
  };

  const handleStartEditReply = (threadUuid: string, replyUuid: string, currentContent: string): void => {
    setEditState({ type: "reply", threadUuid, replyUuid, draft: currentContent });
  };

  const handleEditDraftChange = (value: string): void => {
    if (!editState) return;
    setEditState({ ...editState, draft: value });
  };

  const handleSaveEdit = (): void => {
    if (!editState || !editState.draft.trim() || editState.draft.trim().length > MAX_DISCUSSION_LENGTH) return;
    const trimmed = editState.draft.trim();
    setEditState(null);
    if (editState.type === "thread") {
      Promise.resolve(onEditThreadMessage(editState.threadUuid, trimmed))
        .then(() => {
          showToast("success", "Message updated");
        })
        .catch(() => {
          showToast("error", "Failed to update message");
        });
    } else {
      Promise.resolve(onEditReply(editState.threadUuid, editState.replyUuid, trimmed))
        .then(() => {
          showToast("success", "Reply updated");
        })
        .catch(() => {
          showToast("error", "Failed to update reply");
        });
    }
  };

  const handleCancelEdit = (): void => setEditState(null);

  const handleDeleteThread = (threadUuid: string): void => {
    setPendingDelete({ type: "thread", threadUuid });
  };

  const handleDeleteReply = (threadUuid: string, replyUuid: string): void => {
    setPendingDelete({ type: "reply", threadUuid, replyUuid });
  };

  const handleCancelDelete = (): void => setPendingDelete(null);

  const handleConfirmDelete = (): void => {
    if (!pendingDelete) return;
    if (pendingDelete.type === "thread") {
      Promise.resolve(onDeleteThread(pendingDelete.threadUuid))
        .then(() => showToast("success", "Thread deleted"))
        .catch(() => showToast("error", "Failed to delete thread"));
    } else {
      Promise.resolve(onDeleteReply(pendingDelete.threadUuid, pendingDelete.replyUuid))
        .then(() => showToast("success", "Reply deleted"))
        .catch(() => showToast("error", "Failed to delete reply"));
    }
    setPendingDelete(null);
  };

  const handleResolveThread = (threadUuid: string): void => {
    Promise.resolve(onResolveThread(threadUuid))
      .then(() => {
        showToast("success", "Thread resolved");
      })
      .catch(() => {
        showToast("error", "Failed to resolve thread");
      });
  };

  const handleUnresolveThread = (threadUuid: string): void => {
    Promise.resolve(onUnresolveThread(threadUuid))
      .then(() => {
        showToast("success", "Thread unresolved");
      })
      .catch(() => {
        showToast("error", "Failed to unresolve thread");
      });
  };

  return {
    showResolved,
    openThreadUuid,
    currentUserUuid,
    hasMoreThreads,
    hasMoreResolved,
    newCommentText,
    replyDrafts,
    editState,
    pendingDelete,
    activeThreads,
    resolvedThreads,
    canCreate: permissions.createResource,
    canResolve: permissions.resolveThread !== false,
    toasts,
    toggleShowResolved,
    handleOpenThread,
    handleShowMore,
    handleShowMoreResolved,
    handleNewCommentChange,
    handleAddComment,
    handleReplyDraftChange,
    handleSaveReply,
    handleCancelReply,
    handleStartEditThread,
    handleStartEditReply,
    handleEditDraftChange,
    handleSaveEdit,
    handleCancelEdit,
    handleDeleteThread,
    handleDeleteReply,
    handleConfirmDelete,
    handleCancelDelete,
    handleResolveThread,
    handleUnresolveThread,
    dismissToast,
  };
};
